import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { OpenAIBaseService } from "./open-ai-base.service";
import { ProfileDetails } from "../models/profile.interface";
import { Job } from "../types/job.types";
import { JobMatch } from "../models/job-match.interface";
import { WorkType } from "../types/work-type.enum"; // Import WorkType if needed for mock data

// Define structure for AI response items
interface AIScoreResponse {
  job_id: string;
  ai_score: number;
  ai_reasoning: string;
}

/**
 * Service responsible for using OpenAI to score job matches based on profile and resume.
 */
@Injectable({
  providedIn: "root",
})
export class JobMatchingAIService extends OpenAIBaseService {
  constructor(protected override http: HttpClient) {
    super();
  }

  /**
   * Takes user profile, resume text, and candidate jobs, returns AI-scored matches.
   */
  async getJobMatches(
    profile: ProfileDetails,
    resumeText: string,
    candidateJobs: Job[]
  ): Promise<JobMatch[]> {
    console.log(
      "JobMatchingAIService: Requesting REAL AI scores for",
      candidateJobs.length,
      "candidate jobs."
    );

    // Filter out jobs without an ID before sending to AI
    const validJobsForPrompt = candidateJobs.filter((j) => j.id);
    if (validJobsForPrompt.length === 0) {
      console.log("No valid candidate jobs to send for AI scoring.");
      return []; // Return empty if no valid jobs
    }

    // --- Real Implementation ---
    const systemPrompt = `You are an expert job matching AI. Your goal is to evaluate job descriptions against a user's profile and resume. 
For each provided job, calculate a match score from 0 to 100, where 100 is a perfect match. Provide a concise, one-sentence reasoning for your score, phrased as if you are speaking directly to the user (e.g., \'You appear to be a good match because...\', \'Your profile indicates...\'). Focus on the key factors (skills, experience, location, seniority, responsibilities). 
Strictly output ONLY a valid JSON array of objects. Each object in the array must contain exactly these three fields: 'job_id' (string, matching the input job ID), 'ai_score' (number, 0-100), and 'ai_reasoning' (string). 
Do not include any other text, explanations, or markdown formatting outside the JSON array. Ensure the job_id in your output matches the corresponding job_id provided in the input Candidate Jobs JSON.`;

    // Prepare job data for the prompt (limit details to manage token count)
    const jobsForPrompt = validJobsForPrompt.map((j) => ({
      id: j.id,
      title: j.title,
      company: j.company?.substring(0, 100) || "",
      location:
        j.display_location?.substring(0, 100) ||
        j.location?.substring(0, 100) ||
        "",
      level: j.level,
      short_description: j.short_description?.substring(0, 200) || "",
      responsibilities: j.responsibilities?.slice(0, 5).join("; ") || "",
      requirements: j.requirements?.slice(0, 5).join("; ") || "",
      skills: j.skills?.slice(0, 10).join(", ") || "",
      employment_type: j.employment_type || "",
    }));

    // Limit resume text length to avoid excessive token usage
    const truncatedResumeText = resumeText.substring(0, 2000);

    const userPrompt = `
User Profile:
Location: ${profile.location || "Not Specified"}
Seniority Preference: ${profile.seniority || "Not Specified"}
Target Roles: ${profile.targetRoles?.join(", ") || "Not Specified"}

Resume Text (Truncated):
---
${truncatedResumeText}...
---

Candidate Jobs (JSON):
${JSON.stringify(jobsForPrompt, null, 2)} 

Evaluate each candidate job based on the user profile and resume text. Return the JSON array as described in the system prompt. Focus on relevance of skills, experience level match, role alignment, location preference, and how well the user's experience matches the job responsibilities and required skills.`;

    try {
      console.log("Sending request to OpenAI API...");
      // Ensure OPENAI_API_KEY is available (handled in base service)
      const content = await this.makeOpenAIChatRequest(
        userPrompt,
        systemPrompt,
        "gpt-4o-mini", // Use a capable but cost-effective model
        0.3 // Lower temperature for more deterministic scoring/reasoning
      );
      console.log("Received raw response from OpenAI:", content);

      // Parse the JSON response from AI
      let scoredJobs: AIScoreResponse[] = [];
      try {
        scoredJobs = this.parseJsonResponse(content) as AIScoreResponse[];
        // Basic validation of the parsed structure
        if (
          !Array.isArray(scoredJobs) ||
          (scoredJobs.length > 0 && typeof scoredJobs[0].job_id === "undefined")
        ) {
          console.error(
            "Parsed response is not the expected array format:",
            scoredJobs
          );
          throw new Error("AI response was not in the expected format.");
        }
        console.log(
          `Successfully parsed ${scoredJobs.length} scored job results from AI.`
        );
      } catch (parseError) {
        console.error("Error parsing AI JSON response:", parseError);
        console.error("Raw AI response content:", content); // Log raw content on parse error
        // Try to recover if possible, or throw specific error
        throw new Error(
          `Failed to parse AI response. Raw content: ${content.substring(
            0,
            100
          )}...`
        );
      }

      // Map AI results back to JobMatch objects
      const matches: JobMatch[] = validJobsForPrompt.map((job) => {
        // Find score info, handle potential case differences if any
        const scoreInfo = scoredJobs.find((sj) => sj.job_id === job.id);
        return {
          match_id: `ai-match-${job.id}-${Date.now()}`,
          job_id: job.id!,
          user_id: profile.userId || "unknown-user",
          session_id: "ai-session-" + Date.now(), // TODO: Need a proper session ID mechanism from JobMatcherService
          ai_score: scoreInfo?.ai_score ?? 0, // Default to 0 if not found
          ai_reasoning:
            scoreInfo?.ai_reasoning ??
            "AI analysis could not be completed for this job.",
          status: "presented",
          created_at: new Date().toISOString(),
          job: job, // Attach the full original job data
        };
      });

      // Filter out low-scoring matches
      const SCORE_THRESHOLD = 60;
      const filteredMatches = matches.filter(
        (match) => match.ai_score >= SCORE_THRESHOLD
      );
      console.log(
        `Filtered out ${
          matches.length - filteredMatches.length
        } low-scoring matches (threshold: ${SCORE_THRESHOLD}).`
      );
      console.log(
        "Final AI-scored and filtered matches being returned:",
        filteredMatches
      );

      // Sort the filtered matches
      return filteredMatches.sort((a, b) => b.ai_score - a.ai_score);
    } catch (error: any) {
      console.error("Error getting AI job matches:", error);
      // Provide a more generic error fallback but log specific error
      // Return an empty array or a specific error state? Empty array for now.
      console.error(
        "Failed to get AI scores. Returning empty array. Error details:",
        error.message
      );
      // Optionally, create error JobMatch entries if needed for UI feedback
      /*
      return validJobsForPrompt.map(job => ({
         match_id: `ai-match-error-${job.id}-${Date.now()}`,
         job_id: job.id!,
         user_id: profile.userId || 'unknown-user',
         session_id: "ai-session-error-" + Date.now(),
         ai_score: 0,
         ai_reasoning: 'Error during AI analysis.',
         status: 'error',
         created_at: new Date().toISOString(),
         job: job,
      }));
      */
      return []; // Return empty array on failure
    }
    // --- End Real Implementation ---
  }
}
